"use client";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { MediaInput } from "@/components/transcriptions/MediaPreview";
import * as RadixForm from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { type FileRejectionSummary, summarizeDropRejections } from "@/lib/upload/rejection-utils";
import { DropRejections } from "@/lib/upload/UploadFormFields";
import type { Uploader } from "./multipart-upload";
import { SelectedMediaPreviewPanel, UrlInfoPreviewPanel } from "./UploadMediaPanels";
import type { UploadFormData } from "./upload-form-schema";

export const MediaFormFieldLanding = ({
  form,
  url,
  mediaValue,
  duration,
  sizeMb,
  resetFormFile,
  type,
  mediaPreviewRef,
  canSubmit,
  uploader,
  uploadStatus,
  isLoading,
  urlInfoLoading,
  urlInfo,
  maxSizeMb,
  maxDurationS,
  submitButtonRef,
}: {
  form: UseFormReturn<UploadFormData>;
  url: string | undefined;
  mediaValue: File | null;
  duration: number;
  sizeMb: number;
  resetFormFile: () => void;
  type: string;
  mediaPreviewRef: React.RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  canSubmit: boolean;
  uploader: React.RefObject<Uploader | null>;
  uploadStatus: "INITIAL" | "LOADING" | "ERROR" | "SUCCESS";
  isLoading: boolean;
  urlInfoLoading: boolean;
  urlInfo?: UrlInfo;
  /** Per-file size cap. Drives both the dropzone's `maxSize` and the
   *  "larger than {maxMb}MB" interpolation in the rejection banner. */
  maxSizeMb: number;
  /** Per-file duration cap. Only used for translation interpolation in
   *  this single-file landing flow — no async duration detection yet. */
  maxDurationS: number;
  submitButtonRef?: React.RefObject<HTMLButtonElement | null>;
}) => {
  const t = useTranslations();
  const [rejections, setRejections] = useState<FileRejectionSummary[]>([]);

  return (
    // Tight hero rhythm (same as the app upload form): dropzone, divider and
    // URL field read as one input group, not three separate form sections.
    <div className="space-y-4">
      <RadixForm.FormField
        control={form.control}
        name="mediaInput.files"
        render={({ field }) => {
          return (
            <RadixForm.FormItem>
              <RadixForm.FormLabel className="hidden">
                {t("uploadForm.mediaInputLabel")}
              </RadixForm.FormLabel>
              <RadixForm.FormControl>
                <MediaInput
                  dragOverText={t("uploadForm.dropZoneText")}
                  maxSize={maxSizeMb * 1024 * 1024}
                  maxFiles={1}
                  onReject={(rejected) =>
                    setRejections((prev) => [...prev, ...summarizeDropRejections(rejected)])
                  }
                  onChange={(files) =>
                    field.onChange({
                      target: { value: files, name: field.name },
                    })
                  }
                />
              </RadixForm.FormControl>
              {rejections.length > 0 && (
                <DropRejections
                  rejections={rejections}
                  onDismiss={() => setRejections([])}
                  maxMb={maxSizeMb}
                  max={1}
                  maxHours={Math.floor(maxDurationS / 3600)}
                />
              )}
              <SelectedMediaPreviewPanel
                url={url}
                mediaName={mediaValue?.name}
                duration={duration}
                sizeMb={sizeMb}
                type={type}
                mediaPreviewRef={mediaPreviewRef}
                resetFormFile={resetFormFile}
                canSubmit={canSubmit}
                isLoading={isLoading}
                uploader={uploader}
                uploadStatus={uploadStatus}
                submitLabel={t("uploadForm.beginTranscriptionButton")}
                removeLabel="Remove file"
                submitButtonRef={submitButtonRef}
              />
              <RadixForm.FormMessage />
            </RadixForm.FormItem>
          );
        }}
      />

      <div className="flex items-center gap-4" role="separator">
        <span className="h-px flex-1 bg-border" />
        <span className="text-muted-foreground text-xs uppercase tracking-wider">
          {t("general.or")}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
      <RadixForm.FormField
        control={form.control}
        name="mediaInput.youtubeUrl"
        render={({ field }) => (
          <RadixForm.FormItem className="text-center">
            <RadixForm.FormLabel className="text-lg">
              {t("landingUpload.youtubeUrlInputLabel")}
            </RadixForm.FormLabel>
            <RadixForm.FormControl>
              <div className="relative">
                <Input
                  aria-label={t("landingUpload.youtubeUrlInputLabel")}
                  className="text-center text-base pr-12 sm:text-lg"
                  type="text"
                  placeholder={"https://www.domain.com/file..."}
                  {...field}
                />
                <button
                  className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-1/2"
                  type="button"
                  aria-label="Clear URL"
                  onClick={() => {
                    field.onChange({
                      target: { value: "", name: field.name },
                    });
                  }}
                >
                  <X className="text-foreground/80" />
                </button>
              </div>
            </RadixForm.FormControl>
            <RadixForm.FormDescription>
              {t("uploadForm.youtubeInputDescription")}
            </RadixForm.FormDescription>
            <RadixForm.FormMessage />
            <UrlInfoPreviewPanel
              urlInfoLoading={urlInfoLoading}
              urlInfo={urlInfo}
              canSubmit={canSubmit}
              isLoading={isLoading}
              submitLabel={t("uploadForm.beginTranscriptionButton")}
              submitButtonRef={submitButtonRef}
            />
          </RadixForm.FormItem>
        )}
      />
    </div>
  );
};
